import type { Permission } from '@/lib/rbac/permissions';
import { COLLECTIONS } from './collections';
import { LEAD_TYPES } from './leadTypes';

/**
 * Information architecture: the console is organised around the website itself.
 *
 *   Website          every page, in the order of the site's menu (from the page outlines);
 *                    the open page lists its sections. Editing happens on the page.
 *   Lists            the records pages show (products, projects, articles…), each with the
 *                    page it appears on. Folded away: a record is reached through its page.
 *   Inbox            what visitors sent, with unseen counts.
 *   Media library
 *   Administration   overview, activity, people, tokens, settings.
 *   Advanced         all site text by storage namespace.
 *
 * Each node is gated by the permission that reveals it (hidden if unreadable). The sidebar
 * renders from this + RBAC; the Website tree is filled from GET /admin/page-outlines.
 */
export interface NavLeaf {
  type: 'leaf';
  /** i18n key under nav.* (unless `labelKey` names another). */
  navKey: string;
  /** A full i18n key for the label, when it does not live under nav.*. */
  labelKey?: string;
  to: string;
  permission: Permission;
  /** The console collection it lists: the sidebar says which page shows it. */
  collection?: string;
  /** The lead inbox it opens: the sidebar shows its unseen count. */
  leadType?: string;
}
export interface NavGroup {
  type: 'group';
  navKey: string;
  labelKey?: string;
  children: NavLeaf[];
  /** Folds behind its heading; `open` is the state before the editor chooses. */
  fold?: { open: boolean };
}
/** The website's own pages, filled from the page outlines at run time. */
export interface NavWebsite {
  type: 'website';
  navKey: 'website';
  permission: Permission;
}
export type NavNode = NavGroup | NavLeaf | NavWebsite;

export const NAV: NavNode[] = [
  { type: 'website', navKey: 'website', permission: 'content:read' },
  {
    type: 'group',
    navKey: 'content',
    labelKey: 'website.nav.lists',
    fold: { open: false },
    children: [
      {
        type: 'leaf',
        navKey: 'productFamilies',
        to: '/families',
        permission: 'content:read',
        collection: 'product-families',
      },
      ...COLLECTIONS.map(
        (c): NavLeaf => ({
          type: 'leaf',
          navKey: c.navKey,
          to: `/content/${c.key}`,
          permission: 'content:read',
          collection: c.key,
        }),
      ),
    ],
  },
  {
    type: 'group',
    navKey: 'leads',
    labelKey: 'website.nav.inbox',
    children: LEAD_TYPES.map(
      (t): NavLeaf => ({
        type: 'leaf',
        navKey: t.navKey,
        to: `/leads/${t.key}`,
        permission: 'leads:read',
        leadType: t.key,
      }),
    ),
  },
  { type: 'leaf', navKey: 'media', to: '/media', permission: 'content:read' },
  {
    type: 'group',
    navKey: 'administration',
    fold: { open: false },
    children: [
      {
        type: 'leaf',
        navKey: 'dashboard',
        labelKey: 'website.nav.overview',
        to: '/overview',
        permission: 'content:read',
      },
      { type: 'leaf', navKey: 'audit', to: '/audit', permission: 'audit:read' },
      { type: 'leaf', navKey: 'users', to: '/users', permission: 'users:manage' },
      { type: 'leaf', navKey: 'apiTokens', to: '/tokens', permission: 'users:manage' },
      {
        type: 'leaf',
        navKey: 'aiConnections',
        to: '/ai-connections',
        permission: 'users:manage',
      },
      { type: 'leaf', navKey: 'settings', to: '/settings', permission: 'settings:manage' },
    ],
  },
  {
    type: 'group',
    navKey: 'advanced',
    labelKey: 'website.nav.advanced',
    fold: { open: false },
    children: [
      // Every piece of site text by storage namespace. Everyday edits happen on the page
      // itself; this stays for text no page shows and for search deep links.
      {
        type: 'leaf',
        navKey: 'pageCopy',
        labelKey: 'website.nav.pageCopy',
        to: '/page-copy',
        permission: 'content:read',
      },
    ],
  },
];

/** The first inbox a role may read: where a user who cannot edit the website lands. */
export function firstInbox(can: (p: Permission) => boolean): string | null {
  return can('leads:read') && LEAD_TYPES.length ? `/leads/${LEAD_TYPES[0].key}` : null;
}
